import { useTranslation } from 'react-i18next';
import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import './ProgressionCharts.css';

/** @type {Array<{key: string, label: string, color: string, ref: number|null}>} Available chart metrics with display config. */
const METRICS = [
  { key: 'adr', label: 'ADR', color: '#ff6b00', ref: null },
  { key: 'kd', label: 'K/D', color: '#4ade80', ref: 1.0 },
  { key: 'hs_pct', label: 'HS%', color: '#60a5fa', ref: null },
];

/**
 * Converts raw match data into per-match stat objects (ADR, K/D, HS%) in chronological order.
 * @param {Array<Object>} matches - Match data sorted newest-first.
 * @returns {Array<Object>} Computed stats sorted oldest-first.
 */
function computeStats(matches) {
  // Reverse to chronological order (oldest first)
  const sorted = [...matches].reverse();
  return sorted.map(m => {
    const rounds = (m.team1_score || 0) + (m.team2_score || 0) || 24;
    const kills = m.kills || 0;
    const deaths = m.deaths || 0;
    const damage = m.damage || 0;
    const hsKills = m.head_shot_kills || 0;

    return {
      matchid: m.matchid,
      date: m.start_time
        ? new Date(m.start_time).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })
        : `#${m.matchid}`,
      adr: Math.round((damage / Math.max(rounds, 1)) * 10) / 10,
      kd: Math.round((kills / Math.max(deaths, 1)) * 100) / 100,
      hs_pct: kills > 0 ? Math.round((hsKills / kills) * 100) : 0,
    };
  });
}

/**
 * Adds a moving average field to each data point for the given metric key.
 * @param {Array<Object>} data - Chart data points.
 * @param {string} key - The metric key to average (e.g. "adr").
 * @param {number} [window=5] - Number of data points in the sliding window.
 * @returns {Array<Object>} Data with an added `{key}_ma` field per entry.
 */
function movingAverage(data, key, window = 5) {
  return data.map((d, i) => {
    const start = Math.max(0, i - window + 1);
    const slice = data.slice(start, i + 1);
    const avg = slice.reduce((s, v) => s + v[key], 0) / slice.length;
    return { ...d, [`${key}_ma`]: Math.round(avg * 100) / 100 };
  });
}

/**
 * Line charts showing a player's ADR, K/D, and HS% progression over recent matches,
 * with moving average smoothing and a trend comparison (last 10 vs first 10).
 * @param {Object} props
 * @param {Array<Object>} props.recentMatches - Player's recent match stats (newest first).
 */
export default function ProgressionCharts({ recentMatches }) {
  const { t } = useTranslation();
  const [activeMetric, setActiveMetric] = useState('adr');

  const chartData = useMemo(() => {
    if (!recentMatches || recentMatches.length < 3) return [];
    const stats = computeStats(recentMatches);
    // Apply moving average for all metrics
    let data = stats;
    for (const m of METRICS) {
      data = movingAverage(data, m.key);
    }
    return data;
  }, [recentMatches]);

  if (chartData.length < 3) return null;

  const metric = METRICS.find(m => m.key === activeMetric);
  const maKey = `${activeMetric}_ma`;

  // Compute trend (last 10 vs first 10)
  const last10 = chartData.slice(-10);
  const first10 = chartData.slice(0, 10);
  const avgLast = last10.reduce((s, d) => s + d[activeMetric], 0) / last10.length;
  const avgFirst = first10.reduce((s, d) => s + d[activeMetric], 0) / first10.length;
  const trend = avgLast - avgFirst;
  const trendPct = avgFirst > 0 ? Math.round((trend / avgFirst) * 100) : 0;

  return (
    <div className="progression-charts">
      <div className="progression-header">
        <h3 className="progression-title">{t('profile.progression')}</h3>
        <div className="progression-tabs">
          {METRICS.map(m => (
            <button
              key={m.key}
              className={`progression-tab ${activeMetric === m.key ? 'active' : ''}`}
              style={activeMetric === m.key ? { borderColor: m.color } : {}}
              onClick={() => setActiveMetric(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="progression-trend">
        <span className="progression-current">
          Snitt senaste 10: <strong>{avgLast.toFixed(activeMetric === 'kd' ? 2 : 1)}</strong>
        </span>
        {trendPct !== 0 && (
          <span className={`progression-change ${trendPct > 0 ? 'positive' : 'negative'}`}>
            {trendPct > 0 ? '+' : ''}{trendPct}% {t('profile.vsFirstTen')}
          </span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <XAxis
            dataKey="date"
            tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
            axisLine={{ stroke: 'var(--border-color)' }}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={['auto', 'auto']}
            tick={{ fill: 'var(--text-muted)', fontSize: 10 }}
            axisLine={{ stroke: 'var(--border-color)' }}
            tickLine={false}
            width={40}
          />
          <Tooltip
            contentStyle={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              color: 'var(--text-primary)',
              fontSize: '12px',
            }}
            formatter={(value, name) => {
              if (name === maKey) return [value, `${metric.label} (${t('profile.average')})`];
              if (name === activeMetric) return [value, metric.label];
              return [value, name];
            }}
          />
          {metric.ref != null && (
            <ReferenceLine y={metric.ref} stroke="var(--border-color)" strokeDasharray="3 3" />
          )}
          <Line
            type="monotone"
            dataKey={activeMetric}
            stroke={metric.color}
            strokeWidth={1}
            strokeOpacity={0.3}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey={maKey}
            stroke={metric.color}
            strokeWidth={2}
            dot={false}
            activeDot={{ fill: metric.color, r: 4, stroke: '#fff', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
